import { useState, useEffect } from 'react';
import type { AuctionDetail } from '@/types/domain';
import { formatMoney } from '@/lib/format';
import { parseRublesToKopecks, sanitizeRublesInput } from '@/lib/moneyInput';
import {
  formatBidLimitsHint,
  formatMaxBidExceededError,
  getManualBidRange,
} from '@/lib/bidLimits';
import { Button } from '@/components/ui/Button';
import { usePlaceBid } from '@/hooks/useAuctions';
import { haptic, getTelegramWebApp } from '@/lib/telegram';
import styles from './BidForm.module.css';

interface Props {
  auction: AuctionDetail;
  onOpenAutoBid: () => void;
}

export const BidForm = ({ auction, onOpenAutoBid }: Props) => {
  const bidRange = getManualBidRange(auction);
  const { min: minAllowed, max: maxAllowed, canBid } = bidRange;
  const [amount, setAmount] = useState(minAllowed);
  // Строковое значение поля — отдельно от числового amount (копейки).
  // Нужно, чтобы поле можно было полностью очистить: при value={amount/100}
  // пустой ввод парсился в 0 и инпут нельзя было стереть.
  const [inputValue, setInputValue] = useState(
    canBid ? String(minAllowed / 100) : '',
  );
  const [error, setError] = useState<string | null>(null);

  const placeBid = usePlaceBid(auction.id);

  // Синхронизируем поле с amount (рубли)
  const syncInput = (rubles: number) => {
    setInputValue(String(rubles));
  };

  const clampAmount = (value: number) =>
    Math.min(Math.max(value, minAllowed), maxAllowed);

  // Обновляем поле, если поменялись границы
  useEffect(() => {
    if (!canBid) return;
    if (amount < minAllowed || amount > maxAllowed) {
      const next = clampAmount(amount);
      setAmount(next);
      syncInput(next / 100);
    }
  }, [minAllowed, maxAllowed, canBid]); // eslint-disable-line

  const quickSteps = [
    auction.min_step,
    auction.min_step * 2,
    auction.min_step * 5,
  ];

  const handleQuickAdd = (delta: number) => {
    haptic.selection();
    setAmount((v) => {
      // Если поле пустое — отсчитываем от минимально допустимой ставки
      const base = inputValue.trim() === '' ? minAllowed : v;
      const next = clampAmount(base + delta);
      syncInput(next / 100);
      return next;
    });
    setError(null);
  };

  const handleInputChange = (raw: string) => {
    const sanitized = sanitizeRublesInput(raw);
    setInputValue(sanitized);
    setError(null);
    if (sanitized === '') {
      // Пустое поле — не трогаем amount тут; валидируем при submit
      return;
    }
    const kopecks = parseRublesToKopecks(sanitized);
    if (kopecks !== null) {
      setAmount(kopecks);
    }
  };

  const handleSubmit = async () => {
    if (!canBid) return;

    const tg = getTelegramWebApp();

    setError(null);

    // Пустое или нечисловое поле — просим ввести сумму
    const bidAmount = parseRublesToKopecks(inputValue);
    if (bidAmount === null) {
      setError('Введи сумму ставки');
      haptic.error();
      return;
    }

    if (bidAmount < minAllowed) {
      setError(`Минимум ${formatMoney(minAllowed)}`);
      haptic.error();
      return;
    }
    if (bidAmount > maxAllowed) {
      setError(formatMaxBidExceededError(auction, bidAmount));
      haptic.error();
      return;
    }

    // Подтверждение через нативный popup Telegram
    const confirmed = await new Promise<boolean>((resolve) => {
      tg.showConfirm(
        `Сделать ставку ${formatMoney(bidAmount)}?`,
        (ok) => resolve(ok),
      );
    });

    if (!confirmed) return;

    try {
      await placeBid.mutateAsync(bidAmount);
      haptic.success();
      // Поле сбросится на новый минимум через useEffect
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Не удалось сделать ставку';
      setError(msg);
      haptic.error();
    }
  };

  return (
    <div className={styles.form}>
      {canBid ? (
        <>
          <div className={styles.amountRow}>
            <div className={styles.inputWrap}>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                className={styles.input}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <span className={styles.currency}>₽</span>
            </div>
          </div>

          <div className={styles.quickRow}>
            {quickSteps.map((step) => (
              <button
                key={step}
                type="button"
                className={styles.quickBtn}
                onClick={() => handleQuickAdd(step)}
              >
                +{formatMoney(step)}
              </button>
            ))}
          </div>
        </>
      ) : null}

      <p className={styles.limitsHint}>{formatBidLimitsHint(auction, bidRange)}</p>

      {error && <p className={styles.error}>{error}</p>}

      {canBid ? (
        <div className={styles.actions}>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            loading={placeBid.isPending}
            onClick={handleSubmit}
          >
            {auction.bids_count === 0
              ? `Поставить ${formatMoney(amount)}`
              : `Поднять до ${formatMoney(amount)}`}
          </Button>

          <button className={styles.autoBidLink} onClick={onOpenAutoBid}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
            Настроить автоставку
          </button>
        </div>
      ) : null}
    </div>
  );
};